class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.requestIndex = 0;
    this.activeOption = -1;
    this.searchContent = this.querySelector('.search__content');
    this.status = this.querySelector('[data-search-status]');
    this.resultsElement = this.querySelector('[role="listbox"]');
    this.menuDrawer = this.hasAttribute('data-menu-search-form') ? this.closest('menu-drawer') : null;
    if (this.menuDrawer) {
      this.querySelector('[data-menu-search-back]')?.addEventListener('click', () => this.exitMenuSearch());
      this.menuDrawer.addEventListener('keydown', this.onMenuKeyDown.bind(this), true);
      this.menuDrawer.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape' || !this.menuDrawer.hasAttribute('data-search-active')) return;
        event.preventDefault();
        event.stopPropagation();
        this.exitMenuSearch();
      }, true);
      this.menuDrawer.addEventListener('drawer:handleAfterHide', (event) => {
        if (event.target === this.menuDrawer) this.exitMenuSearch(false);
      });
    }
    this.resetButton?.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', () => {
      this.abortController?.abort();
      this.requestIndex += 1;
      this.resetButton.disabled = this.getQuery().length === 0;
      this.resetSelection();
      this.input.setAttribute('aria-expanded', 'false');
      this.removeAttribute('results');
      this.querySelector('.search__results-all').disabled = true;
      this.resultsElement.innerHTML = '';
      this.refreshDrawerFocus();
      if (this.getQuery().length === 0) this.clear();
      else this.debouncedChange();
    });
    this.debouncedChange = FoxTheme.utils.debounce(this.onChange.bind(this), 200);
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeyDown.bind(this));
    this.addEventListener('submit', (event) => { if (!this.getQuery()) { event.preventDefault(); this.input.focus(); } });
    this.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (!this.contains(document.activeElement)) this.closeSuggestions();
      });
    });
    this.handleOutside = (event) => { if (!this.contains(event.target)) this.closeSuggestions(); };
    if (this.closest('.template-search')) document.addEventListener('click', this.handleOutside);
  }
  disconnectedCallback() {
    this.abortController?.abort();
    document.removeEventListener('click', this.handleOutside);
  }
  get input() { return this.querySelector('input[type="search"]'); }
  get resetButton() { return this.querySelector('button[type="reset"]'); }
  getQuery() { return this.input.value.trim(); }
  onFocus() {
    this.enterMenuSearch();
    if (this.closest('.template-search')) document.body.classList.add('predictive-search-open');
    if (this.getQuery()) this.onChange();
  }
  enterMenuSearch() {
    if (!this.menuDrawer?.open || this.menuDrawer.hasAttribute('data-search-active')) return;
    this.menuDrawer.setAttribute('data-search-active', '');
    const content = this.menuDrawer.querySelector('.menu-drawer__content');
    content.hidden = true;
    content.setAttribute('inert', '');
    // Keep the same panel and focused input. No modal transition or keyboard delay.
    FoxTheme.a11y.removeTrapFocus();
  }
  exitMenuSearch(restoreFocus = true) {
    if (!this.menuDrawer) return;
    this.clear();
    this.menuDrawer.removeAttribute('data-search-active');
    const content = this.menuDrawer.querySelector('.menu-drawer__content');
    content.hidden = false;
    content.removeAttribute('inert');
    if (restoreFocus && this.menuDrawer.open) {
      const activeSubmenu = Array.from(content.querySelectorAll('details[is="menu-drawer-details"][open].active')).pop();
      const destination = activeSubmenu?.querySelector('.menu-drawer__item-back-link') || this.visibleFocusables(content)[0] || this.menuDrawer.querySelector('.drawer__close');
      FoxTheme.a11y.trapFocus(this.menuDrawer, destination);
    }
  }
  visibleFocusables(container) {
    return FoxTheme.a11y.getFocusableElements(container).filter(element =>
      !element.closest('[hidden], [inert]') && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden'
    );
  }
  onMenuKeyDown(event) {
    if (event.key !== 'Tab' || !this.menuDrawer.open || !this.menuDrawer.hasAttribute('data-search-active')) return;
    // The native trap includes hidden navigation links; use only the current search controls.
    const controls = this.visibleFocusables(this.menuDrawer);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && event.target === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && event.target === last) {
      event.preventDefault();
      first?.focus();
    }
  }
  clear(event) {
    event?.preventDefault();
    this.abortController?.abort();
    this.requestIndex += 1;
    this.input.value = '';
    this.resetButton.disabled = true;
    this.removeAttribute('results');
    this.querySelector('.search__results-all').disabled = true;
    this.removeAttribute('loading');
    this.removeAttribute('error');
    this.resetButton.classList.remove('btn--loading');
    this.resultsElement.innerHTML = '';
    this.resultsElement.removeAttribute('aria-busy');
    this.resetSelection();
    this.input.setAttribute('aria-expanded', 'false');
    this.status.textContent = '';
    if (event) this.input.focus();
    this.refreshDrawerFocus();
  }
  setupURL() {
    const url = new URL(FoxTheme.routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[type]', 'product,collection,page,article,query');
    url.searchParams.set('resources[limit]', Math.min(10, Number(this.dataset.resultsLimit) || 10));
    url.searchParams.set('resources[limit_scope]', 'all');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag');
    url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this));
    return url.toString();
  }
  onChange() {
    if (!this.getQuery()) return;
    if (this.closest('.template-search')) document.body.classList.add('predictive-search-open');
    this.renderSection(this.setupURL());
  }
  async renderSection(url) {
    this.abortController?.abort();
    const requestIndex = ++this.requestIndex;
    this.abortController = new AbortController();
    this.setAttribute('loading', 'true');
    this.removeAttribute('error');
    this.resultsElement.setAttribute('aria-busy', 'true');
    this.resetButton.classList.add('btn--loading');
    this.status.textContent = 'Søker …';
    try {
      let responseText = this.cachedMap.get(url);
      if (!responseText) {
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error('Search unavailable');
        responseText = await response.text();
        this.cachedMap.set(url, responseText);
      }
      if (requestIndex !== this.requestIndex || url !== this.setupURL()) return;
      const doc = new DOMParser().parseFromString(responseText, 'text/html');
      const source = doc.getElementById(this.resultsElement.id);
      if (!source) throw new Error('Search results missing');
      this.prioritizeLiteralProducts(source);
      this.resultsElement.innerHTML = source.innerHTML;
      this.setAttribute('results', 'true');
      this.querySelector('.search__results-all').disabled = false;
      this.resetSelection();
      this.input.setAttribute('aria-expanded', 'true');
      const count = this.resultsElement.querySelectorAll('[data-search-option]').length;
      this.status.textContent = count ? `${count} søkeforslag. Bruk piltastene for å velge, eller Enter for alle treff.` : 'Ingen søkeforslag. Prøv et annet søkeord.';
      this.refreshDrawerFocus();
    } catch (error) {
      if (error.name === 'AbortError' || requestIndex !== this.requestIndex) return;
      this.setAttribute('error', 'true');
      this.setAttribute('results', 'true');
      this.querySelector('.search__results-all').disabled = false;
      this.resultsElement.innerHTML = '';
      const message = document.createElement('p');
      message.className = 'll-search-error';
      message.textContent = 'Søkeforslag er midlertidig utilgjengelige. Trykk Enter eller «Se alle søkeresultater» for å søke.';
      this.resultsElement.append(message);
      this.status.textContent = message.textContent;
      this.input.setAttribute('aria-expanded', 'false');
      this.refreshDrawerFocus();
    } finally {
      if (requestIndex === this.requestIndex) {
        this.removeAttribute('loading');
        this.resultsElement.removeAttribute('aria-busy');
        this.resetButton.classList.remove('btn--loading');
      }
    }
  }
  normalizeSearch(value) {
    return value.toLowerCase().replace(/æ/g, 'ae').replace(/ø/g, 'o').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  }
  prioritizeLiteralProducts(source) {
    const terms = this.normalizeSearch(this.getQuery()).split(/\s+/).filter(Boolean);
    if (!terms.length) return;
    const rows = Array.from(source.querySelectorAll('[data-search-option] .ll-search-result[data-search-match]'));
    const matches = rows.filter((row) => {
      const words = this.normalizeSearch(row.dataset.searchMatch).split(/\s+/);
      return terms.every((term) => words.some((word) => word.startsWith(term)));
    });
    // Keep native typo/semantic suggestions when there are no literal matches.
    if (!matches.length) return;
    rows.forEach((row) => {
      if (!matches.includes(row)) row.closest('[data-search-option]').remove();
    });
    source.querySelectorAll('.search__box-item').forEach((group) => {
      if (!group.querySelector('[data-search-option]')) group.remove();
    });
  }
  resetSelection() {
    this.activeOption = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.resultsElement?.querySelectorAll('[aria-selected="true"]').forEach((option) => option.setAttribute('aria-selected', 'false'));
  }
  onKeyDown(event) {
    if (event.key === 'Escape') {
      if (this.menuDrawer?.hasAttribute('data-search-active')) {
        event.preventDefault();
        event.stopPropagation();
      } else this.closeSuggestions();
      return;
    }
    if (!this.hasAttribute('results') || this.input.getAttribute('aria-expanded') !== 'true') return;
    const options = Array.from(this.resultsElement.querySelectorAll('[data-search-option]'));
    if (event.key === 'Enter' && this.activeOption >= 0) {
      event.preventDefault();
      options[this.activeOption]?.querySelector('a')?.click();
      return;
    }
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !options.length) return;
    event.preventDefault();
    const next = this.activeOption === -1 ? (event.key === 'ArrowDown' ? 0 : options.length - 1) : this.activeOption + (event.key === 'ArrowDown' ? 1 : -1);
    this.resetSelection();
    this.activeOption = (next + options.length) % options.length;
    const option = options[this.activeOption];
    option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }
  closeSuggestions() {
    this.input.setAttribute('aria-expanded', 'false');
    this.resetSelection();
    if (this.closest('.template-search')) document.body.classList.remove('predictive-search-open');
  }
  refreshDrawerFocus() {
    if (this.menuDrawer?.open && this.menuDrawer.hasAttribute('data-search-active')) {
      FoxTheme.a11y.removeTrapFocus();
      return;
    }
    const drawer = this.closest('search-drawer[open]');
    if (!drawer) return;
    const active = document.activeElement;
    const focused = this.contains(active) ? active : this.input;
    const start = this.input.selectionStart;
    const end = this.input.selectionEnd;
    FoxTheme.a11y.trapFocus(drawer, focused);
    if (focused === this.input && start !== null) this.input.setSelectionRange(start, end);
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class SearchDrawer extends DrawerComponent {
  get requiresBodyAppended() { return false; }
  get input() { return this.querySelector('input[type="search"]'); }
  get focusElement() { return this.input; }
}
if (!customElements.get('search-drawer')) customElements.define('search-drawer', SearchDrawer);
